import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { IntakeDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { patientMinimalSchema } from "@/src/models/patient";

export const intakeTypeDisplay: Record<IntakeDocumentType, string> = {
  [IntakeDocumentType.INTAKE]: "Intake",
  [IntakeDocumentType.INTAKE_DIALYSIS_QUESTIONAIRE]: "Dialysis Questionaire",
};

export const intakeTypeSchema = z.nativeEnum(IntakeDocumentType);

const baseIntakeFields = {
  patient: patientMinimalSchema,
};

const extendedIntakeSchema = z.object({
  isAdw: isAdwSchema,
  authorizationNumber: z
    .string()
    .nullable()
    .describe(
      `Authorization or referral number associated with the patient's insurance or referral.

     Look for the value next to or below labels like:
     - "Authorization", "Auth #", "Auth Number", "Pre-Auth", "Prior Auth"
     - "Referral #", "Referral Number", "Ref #", "Ref Number"
     - "Certification Number", "Cert #"

     Return only the alphanumeric identifier (may include hyphens).
     Do not confuse with Policy ID, Group Number, Member ID, or Subscriber ID.`
    ),
  dateReceived: DocumentDateSchema.shape.dateReceived,
  dateOfEvent: DocumentDateSchema.shape.dateOfEvent,
  intakeNote: z.string().nullable(),
});

const intakeCustomFields: Record<IntakeDocumentType, Record<string, z.ZodTypeAny>> = {
  [IntakeDocumentType.INTAKE]: extendedIntakeSchema.pick({
    isAdw: true,
    authorizationNumber: true,
    dateReceived: true,
    dateOfEvent: true,
    intakeNote: true,
  }).shape,
  [IntakeDocumentType.INTAKE_DIALYSIS_QUESTIONAIRE]: extendedIntakeSchema.pick({
    dateReceived: true,
    dateOfEvent: true,
    intakeNote: true,
  }).shape,
};

const makeIntakeSchema = (type: IntakeDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseIntakeFields,
    ...customFields,
  });

export const intakeSchemas: Record<IntakeDocumentType, ReturnType<typeof makeIntakeSchema>> = (
  Object.values(IntakeDocumentType) as IntakeDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeIntakeSchema(type, intakeCustomFields[type]);

    return acc;
  },
  {} as Record<IntakeDocumentType, ReturnType<typeof makeIntakeSchema>>
);

const intakeSchemasArray = (Object.values(IntakeDocumentType) as IntakeDocumentType[]).map(
  (type) => intakeSchemas[type]
) as [ReturnType<typeof makeIntakeSchema>, ...ReturnType<typeof makeIntakeSchema>[]];

export const anyIntakeSchema = z.discriminatedUnion("type", intakeSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Intake = ExtractedDocumentFields<IntakeDocumentType, typeof extendedIntakeSchema, typeof baseIntakeFields> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const intakeShape: Intake = {
  id: "",
  type: IntakeDocumentType.INTAKE,
  patient: null,
  authorizationNumber: null,
  dateReceived: null,
  dateOfEvent: null,
  intakeNote: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
